// cards.js — each card is a subdivided plane whose vertices are displaced onto
// the helix curve, so cards hug the curve and connect into one seamless,
// twisting ribbon (adjacent cards share their exact edge points).
import * as THREE from 'three';
import { HELIX, helixPoint } from './helix.js';

const CARD_H    = 1.9;   // world-unit height of the ribbon
const SEG       = 28;    // width subdivisions — more = smoother bend
const CARD_FILL = 0.955;   // fraction of the angular slot the card fills → ~15px gap
const PLANE_ASPECT = (HELIX.R * HELIX.step * CARD_FILL) / CARD_H;   // arc-width : height

// ── breathing ────────────────────────────────────────────────────────────
// A card is not a fixed panel: it contracts as it swings to the back of the
// spiral and stretches open again as it comes round to the front, while the
// picture inside pushes in and pulls back out. Two moves off one driver, so
// they read as one gesture rather than two effects stacked.
//   BACK_FILL / BACK_H  · how far the panel shrinks at the far side
//   BACK_ZOOM           · how hard the picture is pushed in out there
//   SURGE_*             · extra squeeze + push-in while the scroll is moving
const BACK_FILL   = 0.62;
const BACK_H      = 0.88;
const BACK_ZOOM   = 1.42;
const SURGE_FILL  = 0.14;
const SURGE_ZOOM  = 0.16;
const SURGE_REF   = 0.07;   // offset delta per frame that counts as "full speed"

// preallocated scratch — the per-frame loop must never allocate
const _c = { x: 0, y: 0, z: 0 };

// crop-to-fill for the card's resting proportions. The returned pair is the
// baseline every per-frame crop is derived from — the panel changes shape all
// the time now, and the picture must follow by cropping, never by stretching.
function coverFit(tex) {
  const img = tex.image;
  let rx = 1, ry = 1;
  if (img && img.width) {
    const imgAspect = img.width / img.height;
    if (imgAspect > PLANE_ASPECT) rx = PLANE_ASPECT / imgAspect;
    else ry = imgAspect / PLANE_ASPECT;
  }
  tex.repeat.set(rx, ry);
  tex.offset.set((1 - rx) / 2, (1 - ry) / 2);
  return { rx, ry };
}

export async function buildCards(urls, manager) {
  const loader = new THREE.TextureLoader(manager);

  const cards = await Promise.all(urls.map(async (url) => {
    const tex = await loader.loadAsync(url);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.center.set(0.5, 0.5);
    const base = coverFit(tex);

    // unit-width plane; we overwrite XYZ every frame but keep its UVs
    const geo = new THREE.PlaneGeometry(1, CARD_H, SEG, 1);
    const pos = geo.attributes.position;
    // cache each vertex's normalized column (u ∈ -0.5..0.5) and height (v)
    const u = new Float32Array(pos.count);
    const v = new Float32Array(pos.count);
    for (let k = 0; k < pos.count; k++) { u[k] = pos.getX(k); v[k] = pos.getY(k); }

    const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;   // verts live in world space, origin-centred bounds
    return { mesh, geo, pos, u, v, tex, base };
  }));

  const group = new THREE.Group();
  cards.forEach((c) => group.add(c.mesh));

  const n = cards.length;
  const { step } = HELIX;

  let prevOffset = null;
  let surge = 0;                             // eased scroll speed, 0..1

  function update(offset) {
    // speed is read straight off the offset — no plumbing from the scroller,
    // and it decays on its own as the scrub settles
    const dv = prevOffset === null ? 0 : offset - prevOffset;
    prevOffset = offset;
    surge += (Math.min(1, Math.abs(dv) / SURGE_REF) - surge) * .12;

    for (let i = 0; i < n; i++) {
      const { pos, u, v, tex, base } = cards[i];
      const tc = i * step + offset;          // this card's centre parameter

      // z = Rz·sin(t) peaks toward the camera and x = R·cos(t) crosses zero
      // there too, so sin alone says how "front and centre" this card is.
      // Smoothstepped: the panel holds its shape through the swing and then
      // opens sharply as it arrives, instead of easing blandly all the way.
      const s = (Math.sin(tc) + 1) / 2;
      const f = s * s * (3 - 2 * s);
      const squeeze = 1 - surge * SURGE_FILL;
      const fill = CARD_FILL * (BACK_FILL + (1 - BACK_FILL) * f) * squeeze;
      const hs   = (BACK_H + (1 - BACK_H) * f) * squeeze;
      const zoom = BACK_ZOOM - (BACK_ZOOM - 1) * f + surge * SURGE_ZOOM;

      const arr = pos.array;
      for (let k = 0; k < u.length; k++) {
        // sweep t across the card's slot, filling only part of it — the gap to
        // the neighbour widens and closes as the panel breathes
        helixPoint(tc + u[k] * step * fill, _c);
        const j = k * 3;
        arr[j]     = _c.x;
        arr[j + 1] = _c.y + v[k] * hs;       // height rides along world-up
        arr[j + 2] = _c.z;
      }
      pos.needsUpdate = true;

      // the crop follows the panel: fill/hs undo the squeeze so proportions
      // hold, zoom pushes the picture in on top of that
      const rx = base.rx * (fill / CARD_FILL) / zoom;
      const ry = base.ry * hs / zoom;
      tex.repeat.set(rx, ry);
      tex.offset.set((1 - rx) / 2, (1 - ry) / 2);
    }
  }

  function dispose() {
    cards.forEach((c) => { c.geo.dispose(); c.mesh.material.map?.dispose(); c.mesh.material.dispose(); });
  }

  return { group, update, dispose, count: n };
}
